import filesize from 'filesize'

export const getFilesizeFormatter = (
	options = {
		base: 2,
		standard: 'jedec',
		roundingMethod: 'floor',
	},
) => {
	return filesize.partial(options as any)
}

/**
 * Fetches the actual file size by making a full GET request and measuring response size
 * @param url - The URL to check file size for
 * @returns Promise<number> - File size in bytes, or 0 if unable to fetch
 */
export const getFileSizeFromUrl = async (url: string): Promise<number> => {
	try {
		const response = await fetch(url, {
			method: 'GET',
		})

		if (!response.ok) {
			console.warn(`Failed to fetch file for size check: ${url}`)
			return 0
		}

		// First try content-length header
		const contentLength = response.headers.get('content-length')
		if (contentLength && contentLength !== '0') {
			return parseInt(contentLength, 10)
		}

		// If no content-length, read the response and measure it
		const arrayBuffer = await response.arrayBuffer()
		return arrayBuffer.byteLength
	} catch (error) {
		console.warn(`Error fetching file size for URL: ${url}`, error)
		return 0
	}
}

/**
 * Downloads a file and returns both the file size and triggers the browser download
 * @param url - The URL to download
 * @param filename - The filename for the download
 * @returns Promise<number> - File size in bytes
 */
export const downloadFileWithSize = async (
	url: string,
	filename?: string,
): Promise<number> => {
	try {
		const response = await fetch(url)

		if (!response.ok) {
			throw new Error(`Failed to fetch file: ${response.statusText}`)
		}

		// Get the blob and its size
		const blob = await response.blob()
		const fileSize = blob.size

		// Create download link and trigger download
		const downloadUrl = window.URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = downloadUrl
		a.download = filename || 'download'
		document.body.appendChild(a)
		a.click()
		document.body.removeChild(a)
		window.URL.revokeObjectURL(downloadUrl)

		return fileSize
	} catch (error) {
		console.error(`Error downloading file: ${url}`, error)
		// Fallback to JsFileDownloader if our method fails
		const JsFileDownloader = (await import('js-file-downloader')).default
		new JsFileDownloader({
			url,
			filename,
			autoStart: true,
			forceDesktopMode: true,
		})
		return 0
	}
}

/**
 * Extracts filename from a URL (handles both complete and relative URLs)
 * @param url - The URL to extract filename from
 * @returns filename or undefined if not found
 */
export const extractFilenameFromUrl = (url: string): string | undefined => {
	try {
		let pathname: string

		// Check if URL is complete (has protocol)
		if (url.startsWith('http://') || url.startsWith('https://')) {
			// Complete URL - use URL constructor
			const urlObj = new URL(url)
			pathname = urlObj.pathname
		} else {
			// Relative/incomplete URL - use as pathname directly
			pathname = url
		}

		const filename = pathname.split('/').pop()
		return filename && filename.includes('.')
			? decodeURIComponent(filename)
			: undefined
	} catch {
		return undefined
	}
}
